import { useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { fetchAdminContent, savePageContent } from "../../api/content";
import {
  getCurrentUser,
  getStoredSession,
  isCurrentUserAdmin,
  signIn,
  signOut,
} from "../../api/supabase";
import { defaultAboutContent, defaultMediaKitContent, defaultPresskitContent } from "../../content/defaultContent";
import AboutEditor from "./AboutEditor";
import AdminLayout from "./AdminLayout";
import AdminLogin from "./AdminLogin";
import PresskitEditor from "./PresskitEditor";
import PresskitMediaManager from "./PresskitMediaManager";
import MediaKitEditor from "./MediaKitEditor";
import LessonRequests from "./LessonRequests";
import { useAdminTheme } from "./adminTheme";
import "./Admin.css";

const paragraphsToText = (paragraphs = []) => paragraphs.join("\n\n");
const textToParagraphs = (text) => text.split(/\n\s*\n/).map((paragraph) => paragraph.trim()).filter(Boolean);

function mergeContent(content = {}) {
  return {
    about: { ...defaultAboutContent, ...content.about },
    presskit: { ...defaultPresskitContent, ...content.presskit },
    mediakit: { ...defaultMediaKitContent, ...content.mediakit },
  };
}

export default function Admin() {
  const { theme, toggleTheme } = useAdminTheme();
  const [user, setUser] = useState(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const [content, setContent] = useState(() => mergeContent());
  const [savedContent, setSavedContent] = useState(() => mergeContent());
  const [dirtyPages, setDirtyPages] = useState(() => new Set());
  const [aboutStoryDraft, setAboutStoryDraft] = useState(() => paragraphsToText(defaultAboutContent.storyParagraphs));
  const [presskitLongBioDraft, setPresskitLongBioDraft] = useState(() => paragraphsToText(defaultPresskitContent.longBioParagraphs));
  const [loadingContent, setLoadingContent] = useState(true);
  const [savingPage, setSavingPage] = useState("");
  const [status, setStatus] = useState({ type: "", message: "" });

  useEffect(() => {
    if (!getStoredSession()) {
      setCheckingSession(false);
      return;
    }

    Promise.all([getCurrentUser(), isCurrentUserAdmin()])
      .then(([currentUser, isAdmin]) => {
        if (!isAdmin) throw new Error("This account is not authorized to manage the site.");
        setUser(currentUser);
      })
      .catch(async (error) => {
        await signOut();
        setStatus({ type: "error", message: error.message });
      })
      .finally(() => setCheckingSession(false));
  }, []);

  useEffect(() => {
    if (!user) return;
    setLoadingContent(true);
    fetchAdminContent()
      .then((savedContent) => {
        const merged = mergeContent(savedContent);
        setContent(merged);
        setSavedContent(merged);
        setDirtyPages(new Set());
        setAboutStoryDraft(paragraphsToText(merged.about.storyParagraphs));
        setPresskitLongBioDraft(paragraphsToText(merged.presskit.longBioParagraphs));
      })
      .catch((error) => setStatus({ type: "error", message: error.message }))
      .finally(() => setLoadingContent(false));
  }, [user]);

  useEffect(() => {
    if (!dirtyPages.size) return undefined;
    const warnBeforeLeaving = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => window.removeEventListener("beforeunload", warnBeforeLeaving);
  }, [dirtyPages]);

  const markDirty = (page) => setDirtyPages((current) => {
    const next = new Set(current);
    next.add(page);
    return next;
  });

  const clearDirty = (page) => setDirtyPages((current) => {
    const next = new Set(current);
    next.delete(page);
    return next;
  });

  const updatePage = (page, field, value) => {
    markDirty(page);
    setContent((current) => ({
      ...current,
      [page]: { ...current[page], [field]: value },
    }));
  };

  const updateStat = (index, field, value) => {
    markDirty("about");
    setContent((current) => ({
      ...current,
      about: {
        ...current.about,
        stats: current.about.stats.map((stat, statIndex) =>
          statIndex === index ? { ...stat, [field]: value } : stat
        ),
      },
    }));
  };

  async function handleLogin(event) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    setSavingPage("login");
    setStatus({ type: "", message: "" });
    try {
      const session = await signIn(data.get("email"), data.get("password"));
      if (!(await isCurrentUserAdmin())) {
        await signOut();
        throw new Error("This account is not authorized to manage the site.");
      }
      setLoadingContent(true);
      setUser(session.user);
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    } finally {
      setSavingPage("");
    }
  }

  async function handleSave(event, page) {
    event.preventDefault();
    setSavingPage(page);
    setStatus({ type: "", message: "" });
    try {
      const pendingContent = page === "about"
        ? { ...content.about, storyParagraphs: textToParagraphs(aboutStoryDraft) }
        : page === "presskit"
          ? { ...content.presskit, longBioParagraphs: textToParagraphs(presskitLongBioDraft) }
          : content.mediakit;
      const saved = await savePageContent(page, pendingContent);
      setContent((current) => ({ ...current, [page]: saved }));
      setSavedContent((current) => ({ ...current, [page]: saved }));
      clearDirty(page);
      if (page === "about") setAboutStoryDraft(paragraphsToText(saved.storyParagraphs));
      if (page === "presskit") setPresskitLongBioDraft(paragraphsToText(saved.longBioParagraphs));
      setStatus({
        type: "success",
        message: `${page === "about" ? "About" : page === "presskit" ? "Press Kit" : "Media Kit"} content published successfully.`,
      });
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    } finally {
      setSavingPage("");
    }
  }

  async function handleSignOut() {
    if (dirtyPages.size && !window.confirm("Discard all unsaved admin changes and sign out?")) return;
    await signOut();
    setUser(null);
    setLoadingContent(true);
    setContent(mergeContent());
    setSavedContent(mergeContent());
    setDirtyPages(new Set());
    setAboutStoryDraft(paragraphsToText(defaultAboutContent.storyParagraphs));
    setPresskitLongBioDraft(paragraphsToText(defaultPresskitContent.longBioParagraphs));
    setStatus({ type: "", message: "" });
  }

  function discardPage(page) {
    const saved = savedContent[page];
    setContent((current) => ({ ...current, [page]: saved }));
    if (page === "about") setAboutStoryDraft(paragraphsToText(saved.storyParagraphs));
    if (page === "presskit") setPresskitLongBioDraft(paragraphsToText(saved.longBioParagraphs));
    clearDirty(page);
    setStatus({ type: "success", message: `${page === "about" ? "About" : page === "presskit" ? "Press Kit" : "Media Kit"} changes discarded.` });
  }

  if (checkingSession) {
    return <main className="admin-page" data-theme={theme}><p className="admin-loading">Checking session…</p></main>;
  }

  if (!user) {
    return <AdminLogin theme={theme} onToggleTheme={toggleTheme} busy={savingPage === "login"} onSubmit={handleLogin} status={status} />;
  }

  return (
    <AdminLayout theme={theme} onToggleTheme={toggleTheme} email={user.email} dirtyPages={dirtyPages} onDismissStatus={() => setStatus({ type: "", message: "" })} onSignOut={handleSignOut} status={status}>
      {loadingContent ? <p className="admin-loading">Loading page content…</p> : (
        <Routes>
          <Route index element={<Navigate to="about" replace />} />
          <Route path="about" element={(
            <AboutEditor
              content={content.about}
              storyDraft={aboutStoryDraft}
              saving={savingPage === "about"}
              dirty={dirtyPages.has("about")}
              onChange={(field, value) => updatePage("about", field, value)}
              onDiscard={() => discardPage("about")}
              onStoryChange={(value) => { setAboutStoryDraft(value); markDirty("about"); }}
              onStatChange={updateStat}
              onSubmit={(event) => handleSave(event, "about")}
            />
          )} />
          <Route path="presskit" element={(
            <PresskitEditor
              content={content.presskit}
              longBioDraft={presskitLongBioDraft}
              saving={savingPage === "presskit"}
              dirty={dirtyPages.has("presskit")}
              onChange={(field, value) => updatePage("presskit", field, value)}
              onDiscard={() => discardPage("presskit")}
              onLongBioChange={(value) => { setPresskitLongBioDraft(value); markDirty("presskit"); }}
              onSubmit={(event) => handleSave(event, "presskit")}
            />
          )} />
          <Route path="media" element={<PresskitMediaManager />} />
          <Route path="lessons" element={<LessonRequests />} />
          <Route path="media-kit" element={(
            <MediaKitEditor
              content={content.mediakit}
              saving={savingPage === "mediakit"}
              dirty={dirtyPages.has("mediakit")}
              onChange={(field, value) => updatePage("mediakit", field, value)}
              onDiscard={() => discardPage("mediakit")}
              onSubmit={(event) => handleSave(event, "mediakit")}
            />
          )} />
          <Route path="*" element={<Navigate to="about" replace />} />
        </Routes>
      )}
    </AdminLayout>
  );
}
